import { useState } from 'react';

import { btn, field, text } from '../ui';
import {
  emailIsValid,
  sortTeamRows,
  teamRows,
  toClrRoster,
  toEmailMap,
  toRoster,
  zoneColor,
} from '../schedule.mjs';

const th = 'text-left font-medium text-gray-500 px-3 py-2';
const td = 'px-3 py-2 border-t border-gray-200';

export default function TeamPanel({ emailToName, roster, zoneNames, clrRoster = [], onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);

  // Read-only rows come straight from props, so they cannot go stale.
  const rows = editing ? draft : sortTeamRows(teamRows(emailToName, roster, clrRoster), zoneNames);

  const trimmed = draft.map((r) => r.name.trim()).filter(Boolean);
  const duplicates = [...new Set(trimmed.filter((n, i) => trimmed.indexOf(n) !== i))];
  const invalidEmails = draft.filter((r) => r.email.trim() && !emailIsValid(r.email));
  const blocked = duplicates.length > 0 || invalidEmails.length > 0;

  const zoneIndex = (zone) => zoneNames.indexOf(zone);

  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <label className={field.label}>Team members</label>
        {!editing ? (
          <button
            onClick={() => {
              setDraft(sortTeamRows(teamRows(emailToName, roster, clrRoster), zoneNames));
              setEditing(true);
            }}
            className={btn.primary}
          >
            Edit team
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => {
                onSave(
                  toEmailMap(draft),
                  toRoster(draft, zoneNames, roster),
                  toClrRoster(draft, clrRoster)
                );
                setEditing(false);
              }}
              disabled={blocked}
              className={`${btn.save} ${blocked ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Save team
            </button>
            <button onClick={() => setEditing(false)} className={btn.cancel}>Cancel</button>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-500 mb-3">
        Who covers which zone and who takes a turn as customer lead. The email is the address on
        their calendar events, used so PTO import knows who is off, and can be left blank. CLR is
        the weekly customer lead rotation; ticking someone new adds them at the end of it.
      </p>

      <div className="border rounded-lg overflow-hidden max-w-4xl">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className={`${th} w-48`}>Name</th>
              <th className={th}>Email</th>
              <th className={`${th} w-32`}>Zone</th>
              <th className={`${th} w-20 text-center`}>CLR</th>
              {editing && <th className={`${th} w-16`}></th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) =>
              editing ? (
                <tr key={i}>
                  <td className={td}>
                    <input
                      value={row.name}
                      onChange={(e) =>
                        setDraft(draft.map((r, j) => (j === i ? { ...r, name: e.target.value } : r)))
                      }
                      placeholder="Name"
                      className={`${field.inputSm} w-full ${
                        row.name.trim() && duplicates.includes(row.name.trim())
                          ? 'border-red-400 bg-red-50'
                          : ''
                      }`}
                    />
                  </td>
                  <td className={td}>
                    <input
                      type="email"
                      value={row.email}
                      onChange={(e) =>
                        setDraft(draft.map((r, j) => (j === i ? { ...r, email: e.target.value } : r)))
                      }
                      placeholder="person@min.io"
                      aria-invalid={row.email.trim() !== '' && !emailIsValid(row.email)}
                      className={`${field.inputSm} w-full ${
                        row.email.trim() && !emailIsValid(row.email) ? 'border-red-400 bg-red-50' : ''
                      }`}
                    />
                  </td>
                  <td className={td}>
                    <select
                      value={row.zone}
                      onChange={(e) =>
                        setDraft(draft.map((r, j) => (j === i ? { ...r, zone: e.target.value } : r)))
                      }
                      className={`${field.inputSm} w-full`}
                    >
                      <option value="">unassigned</option>
                      {zoneNames.map((z) => (
                        <option key={z} value={z}>{z}</option>
                      ))}
                    </select>
                  </td>
                  <td className={`${td} text-center`}>
                    <input
                      type="checkbox"
                      checked={row.clr}
                      title="In the CLR rotation"
                      onChange={(e) =>
                        setDraft(draft.map((r, j) => (j === i ? { ...r, clr: e.target.checked } : r)))
                      }
                    />
                  </td>
                  <td className={`${td} text-right`}>
                    <button
                      onClick={() => setDraft(draft.filter((_, j) => j !== i))}
                      className={btn.danger}
                    >
                      remove
                    </button>
                  </td>
                </tr>
              ) : (
                <tr key={row.name || i} className="hover:bg-gray-50">
                  <td className={`${td} font-medium`}>{row.name}</td>
                  <td className={`${td} text-gray-600`}>
                    {row.email || <span className={text.hint}>none</span>}
                  </td>
                  <td className={td}>
                    {row.zone ? (
                      <span className={`px-2 py-0.5 rounded text-xs ${zoneColor(zoneIndex(row.zone))}`}>
                        {row.zone}
                      </span>
                    ) : (
                      <span className={text.hint}>unassigned</span>
                    )}
                  </td>
                  <td className={`${td} text-center`}>
                    <input
                      type="checkbox"
                      checked={row.clr}
                      disabled
                      readOnly
                      aria-label={`${row.name} ${row.clr ? 'is' : 'is not'} in the CLR rotation`}
                    />
                  </td>
                </tr>
              )
            )}
            {rows.length === 0 && (
              <tr>
                <td className={`${td} ${text.hint}`} colSpan={editing ? 5 : 4}>Nobody yet</td>
              </tr>
            )}
          </tbody>
        </table>

        {editing && (
          <div className="border-t border-gray-200 px-3 py-2">
            <button
              onClick={() => setDraft([...draft, { email: '', name: '', zone: '', clr: false }])}
              className={btn.link}
            >
              + Add person
            </button>
          </div>
        )}
      </div>

      <p className={text.hintTight}>
        {rows.length} {rows.length === 1 ? 'person' : 'people'},{' '}
        {rows.filter((r) => r.clr).length} in the CLR rotation
      </p>

      {editing && duplicates.length > 0 && (
        <p className="text-sm text-red-600 mt-2">
          Two rows share the name {duplicates.join(', ')}. Names identify people across zones, PTO
          and CLR, so they have to be unique.
        </p>
      )}
      {editing && invalidEmails.length > 0 && (
        <p className="text-sm text-red-600 mt-2">
          Fix {invalidEmails.length} invalid email address
          {invalidEmails.length === 1 ? '' : 'es'} before saving.
        </p>
      )}
    </div>
  );
}
